import classNames from "classnames";
import { Clock } from "lucide-react";
import type { ReactNode } from "react";

import type { FilterDefinition } from "@/shared/components/filters/FilterDefinition";
import type { FilterComparator } from "@/shared/components/filters/FilterSchema";
import {
  comparatorLabel,
  spokenComparatorLabel,
} from "@/shared/components/filters/operators";

import styles from "./OperatorPicker.module.css";

/**
 * Leading icon for a type's preset pills. A clock reads as "relative to now",
 * which is true of the date relatives and of nothing else — identity's
 * `IS_ANY_<TYPE>` presets are whole-type matches with no time axis, so they
 * render bare.
 */
const PRESET_ICONS: Partial<Record<FilterDefinition["type"], ReactNode>> = {
  date: <Clock className={styles.presetIcon} aria-hidden="true" />,
};

interface OperatorPickerProps {
  fieldType: FilterDefinition["type"];
  value: FilterComparator;
  onChange: (next: FilterComparator) => void;
  /**
   * The value-bearing comparators to render as pills — the field's effective
   * set (see `effectiveComparators`).
   */
  operators: readonly FilterComparator[];
  /**
   * The valueless preset pills, rendered as a second row (see
   * `effectivePresets`). Both lists are per-field rather than per-type, so a
   * field that admits only some of its type's presets — an `identity` column
   * declaring one principal type — gets only those.
   */
  presets: readonly FilterComparator[];
}

/**
 * Pill-shaped rows of operator buttons: the field's value-bearing comparators,
 * then its presets on their own row. Each is labeled via `comparatorLabel`;
 * the selected pill is filled, the rest are quiet outlines. Numeric symbol
 * labels (=, ≠, ≤, …) get a spoken `aria-label` so screen readers announce
 * them deterministically.
 */
export function OperatorPicker({
  fieldType,
  value,
  onChange,
  operators,
  presets,
}: OperatorPickerProps) {
  return (
    <div className={styles.picker} role="radiogroup" aria-label="Operator">
      <div className={styles.row}>
        {operators.map((operator) => (
          <Pill
            key={operator}
            operator={operator}
            fieldType={fieldType}
            isSelected={operator === value}
            onSelect={() => onChange(operator)}
          />
        ))}
      </div>
      {presets.length > 0 ? (
        <div className={styles.row}>
          {presets.map((operator) => (
            <Pill
              key={operator}
              operator={operator}
              fieldType={fieldType}
              isSelected={operator === value}
              onSelect={() => onChange(operator)}
              className={styles.pillPreset}
              icon={PRESET_ICONS[fieldType]}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface PillProps {
  operator: FilterComparator;
  fieldType: FilterDefinition["type"];
  isSelected: boolean;
  onSelect: () => void;
  /** Caller-applied class composed with the base pill / selected styles. */
  className?: string;
  /** Optional leading icon rendered before the label (e.g. presets show a clock). */
  icon?: ReactNode;
}

function Pill({
  operator,
  fieldType,
  isSelected,
  onSelect,
  className,
  icon,
}: PillProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      // Numeric pills render as symbols (=, ≠, ≤, …) which screen
      // readers narrate inconsistently. Always provide an explicit
      // phrase form so SR users get deterministic announcement.
      aria-label={spokenComparatorLabel(fieldType, operator)}
      className={classNames(
        styles.pill,
        { [styles.pillSelected]: isSelected },
        className,
      )}
      onClick={onSelect}
    >
      {icon}
      {comparatorLabel(fieldType, operator)}
    </button>
  );
}
